/**
 * Tool Registry — Public API
 *
 * Powered by the declarative nav-tree. All data is auto-derived from tree.tsx.
 * 사이드바, 대시보드, 랜딩 페이지, AI 비서가 이 파일을 참조합니다.
 */
import { Search, Users, FileText, Megaphone, Activity } from "lucide-react";
import { NAV_TREE, HIDDEN_ROUTE_PATHS, flatten } from "./nav-tree";

/* ═══ Types (backward-compatible exports) ═══ */

export type ToolCategory = "smart" | "data" | "automation";

export interface CategoryMeta {
  label: string;
  desc: string;
  badgeClass: string;
  badgeDot: string;
}

export interface DomainMeta {
  label: string;
  desc: string;
  basePath: string;
  icon: React.ReactNode;
}

export interface AssistantMeta {
  assistantIntents?: string[];
  assistantPrompt?: string;
}

export interface NavigationMeta {
  navGroup?: string;
  navLabel?: string;
}

export interface ToolSource extends AssistantMeta, NavigationMeta {
  label: string;
  desc: string;
  to: string;
  icon: React.ReactNode;
  category: ToolCategory;
  domain: string;
  ai?: boolean;
}

export type FeaturePath =
  | "home"
  | "overview"
  | "explorer"
  | "analysis"
  | "tracking"
  | "workspace"
  | "data"
  | "diagnostics"
  | "campaigns"
  | "optimization"
  | "keywords"
  | "anomalies"
  | "quality"
  | string;

export type RouteEntryKind = "domain-home" | "feature-home" | "feature-child";

export interface RouteEntry extends ToolSource {
  domain: DomainKey;
  domainPath: string;
  featurePath: FeaturePath;
  featureLabel: string;
  shortLabel: string;
  kind: RouteEntryKind;
}

/* ─── Category Metadata ─── */

export const CATEGORY_META: Record<ToolCategory, CategoryMeta> = {
  smart: {
    label: "스마트",
    desc: "실시간 분석 & AI",
    badgeClass: "bg-red-500 text-white",
    badgeDot: "bg-red-500",
  },
  data: {
    label: "데이터",
    desc: "DB 대량 조회·테이블",
    badgeClass: "bg-gray-500 text-white",
    badgeDot: "bg-gray-400",
  },
  automation: {
    label: "자동화",
    desc: "모니터링 & 자동화",
    badgeClass: "bg-amber-500 text-white",
    badgeDot: "bg-amber-500",
  },
};

/* ─── Domain Groups ─── */

export const DOMAIN_ORDER = [
  "키워드",
  "채널",
  "콘텐츠",
  "검색광고",
  "인사이트",
] as const;

export type DomainKey = (typeof DOMAIN_ORDER)[number];

export const DOMAIN_META: Record<DomainKey, DomainMeta> = {
  키워드: {
    label: "키워드",
    desc: "검색키워드 분석과 검색결과 구성을 한눈에 파악합니다",
    basePath: "/keywords",
    icon: <Search className="size-5" />,
  },
  채널: {
    label: "채널",
    desc: "블로그, 카페, 인플루언서 등 채널을 진단하고 성장을 관리합니다",
    basePath: "/channels",
    icon: <Users className="size-5" />,
  },
  콘텐츠: {
    label: "콘텐츠",
    desc: "콘텐츠 작성, 검색 노출 점검, 발행 캘린더를 관리합니다",
    basePath: "/contents",
    icon: <FileText className="size-5" />,
  },
  검색광고: {
    label: "검색광고",
    desc: "네이버 검색광고의 키워드 선정부터 캠페인 관리까지 지원합니다",
    basePath: "/search-marketing",
    icon: <Megaphone className="size-5" />,
  },
  인사이트: {
    label: "인사이트",
    desc: "이상 징후 감지와 콘텐츠 품질 관리로 채널 건강을 지킵니다",
    basePath: "/insights",
    icon: <Activity className="size-5" />,
  },
};

/* ═══ Tree → Flat (auto-derived) ═══ */

const _flat = flatten(NAV_TREE, HIDDEN_ROUTE_PATHS);

export const TOOL_SOURCES: ToolSource[] = _flat.toolSources;
export const TOOLS = TOOL_SOURCES;

/** All route paths — used by config.ts to auto-build PREFIXED_ROUTE_PATHS */
export const ROUTE_PATHS: ReadonlySet<string> = _flat.routePaths;

/* ═══ Helpers ═══ */

function getFeaturePath(t: ToolSource): FeaturePath {
  const basePath = DOMAIN_META[t.domain as DomainKey]?.basePath;
  if (!basePath) return "overview";
  if (t.to === basePath) return "overview";
  // /explore 처럼 basePath 밖 경로 처리
  if (!t.to.startsWith(basePath + "/") && t.to !== basePath) {
    return t.to.replace(/^\/+/, "").split("/")[0] || "overview";
  }
  const rest = t.to.slice(basePath.length).replace(/^\/+/, "");
  const [feature = "overview"] = rest.split("/");
  return feature;
}

function normalizeTool(t: ToolSource): RouteEntry {
  const domain = t.domain as DomainKey;
  const domainMeta = DOMAIN_META[domain];
  const featureLabel = t.navGroup ?? t.label;
  return {
    ...t,
    domain,
    domainPath: domainMeta.basePath,
    featurePath: getFeaturePath(t),
    featureLabel,
    shortLabel: t.navLabel ?? t.label,
    kind: t.navGroup
      ? "feature-child"
      : t.to === domainMeta.basePath
        ? "domain-home"
        : "feature-home",
  };
}

export const ROUTE_ENTRIES: RouteEntry[] = TOOL_SOURCES.map(normalizeTool);

/* ─── Sidebar nav structure ─── */

export interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  category: ToolCategory;
  ai?: boolean;
  navigable?: boolean;
  children?: { label: string; to: string }[];
}

export interface NavSection {
  domain: DomainKey;
  meta: DomainMeta;
  items: NavItem[];
}

export interface FeatureGroup {
  domain: DomainKey;
  domainMeta: DomainMeta;
  featureLabel: string;
  featurePath: FeaturePath;
  featureHome?: RouteEntry;
  children: RouteEntry[];
}

export interface AssistantRecommendation {
  entry: RouteEntry;
  score: number;
  matchedIntents: string[];
  rationale: string;
}

/** Build sidebar nav sections — now directly from tree flatten result */
export function buildNavSections(): NavSection[] {
  return _flat.navSections.map((section) => ({
    domain: section.domain as DomainKey,
    meta: DOMAIN_META[section.domain as DomainKey],
    items: section.items,
  }));
}

/** Get tools grouped by domain */
export function getToolsByDomain(): {
  domain: DomainKey;
  meta: DomainMeta;
  tools: RouteEntry[];
}[] {
  const map = new Map<DomainKey, RouteEntry[]>();
  for (const t of ROUTE_ENTRIES) {
    const arr = map.get(t.domain) || [];
    arr.push(t);
    map.set(t.domain, arr);
  }
  return DOMAIN_ORDER.filter((d) => map.has(d)).map((d) => ({
    domain: d,
    meta: DOMAIN_META[d],
    tools: map.get(d)!,
  }));
}

function groupEntriesByFeature(tools: RouteEntry[]) {
  const featureMap = new Map<string, RouteEntry[]>();
  const featureOrder: string[] = [];
  for (const t of tools) {
    if (!featureMap.has(t.featureLabel)) {
      featureMap.set(t.featureLabel, []);
      featureOrder.push(t.featureLabel);
    }
    featureMap.get(t.featureLabel)!.push(t);
  }
  return { featureMap, featureOrder };
}

export function getFeaturesByDomain(): {
  domain: DomainKey;
  meta: DomainMeta;
  features: FeatureGroup[];
}[] {
  return getToolsByDomain().map(({ domain, meta, tools }) => {
    const { featureMap, featureOrder } = groupEntriesByFeature(tools);
    const features = featureOrder.map((featureLabel) => {
      const entries = featureMap.get(featureLabel)!;
      const featureHome = entries.find(
        (entry) => entry.kind !== "feature-child",
      );
      const firstEntry = featureHome ?? entries[0]!;
      return {
        domain,
        domainMeta: meta,
        featureLabel,
        featurePath: firstEntry.featurePath,
        featureHome,
        children: entries.filter((entry) => entry.kind === "feature-child"),
      };
    });
    return { domain, meta, features };
  });
}

export function countByCategory(): Record<ToolCategory, number> {
  const counts: Record<ToolCategory, number> = {
    smart: 0,
    data: 0,
    automation: 0,
  };
  for (const t of ROUTE_ENTRIES) counts[t.category]++;
  return counts;
}

export function getAITools(): RouteEntry[] {
  return ROUTE_ENTRIES.filter((t) => t.ai);
}

export function getAssistantStarters(limit = 4): string[] {
  return ROUTE_ENTRIES.filter((entry) => entry.assistantPrompt)
    .slice(0, limit)
    .map((entry) => entry.assistantPrompt!);
}

export function getAssistantRecommendations(
  prompt: string,
  limit = 3,
): AssistantRecommendation[] {
  const normalized = prompt.trim().toLowerCase();
  const candidates = ROUTE_ENTRIES.filter(
    (entry) => (entry.assistantIntents?.length ?? 0) > 0,
  );

  const scored = candidates
    .map((entry) => {
      const matchedIntents = (entry.assistantIntents ?? []).filter((intent) =>
        normalized.includes(intent.toLowerCase()),
      );
      const score =
        matchedIntents.length * 3 +
        (entry.featurePath === "workspace" ? 2 : 0) +
        (entry.ai ? 1 : 0);
      return {
        entry,
        score,
        matchedIntents,
        rationale:
          matchedIntents.length > 0
            ? `${matchedIntents.join(", ")} 요청과 직접 연결됩니다.`
            : "assistant 기본 추천 흐름에서 우선순위가 높은 작업입니다.",
      };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length > 0) return scored.slice(0, limit);

  return candidates
    .filter(
      (entry) =>
        entry.featurePath === "workspace" ||
        entry.to === "/keywords/analysis/clusters",
    )
    .slice(0, limit)
    .map((entry, index) => ({
      entry,
      score: limit - index,
      matchedIntents: [],
      rationale: "초기 탐색에 적합한 대표 작업입니다.",
    }));
}
